import MergeIcon from "@mui/icons-material/Merge";
import {
  Box,
  Button,
  List,
  ListItem,
  ListItemText,
  ListSubheader,
  Paper,
  IconButton,
  useTheme,
  createTheme,
  ThemeProvider,
} from "@mui/material";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Tooltip from "@mui/material/Tooltip";
import React, { useRef, useState } from "react";
import { MERGE_TICKETS_URL } from "../../Constants/Constants";
import { MERGED } from "../../Constants/StatusConstants";
import AxiosInstance from "../../services/AxiosInstance";
import { useAlert } from "./../alerts/AlertContext";
import { isEmpty, ROLE_USER } from "../../Constants/RolesConstants";
import { STATUS_CLOSED } from "../../Constants/StatusConstants";

const MergeTicketDialog = ({
  mergeTicketsDataIds,
  callTickets,
  getTickets,
  componentPermissions,
  userRole,
}) => {
  const globaltheme = useTheme();
  const theme = createTheme({
    ...globaltheme,
    components: {},
  });
  const mergeTicketsData = getTickets(mergeTicketsDataIds);
  const [disableDialog, setDisableDialog] = useState(false);
  const [open, setOpen] = useState(false);
  const showAlert = useAlert();

  const childItems = useRef([]);

  // State for available (left) and selected (right) items
  const [left, setLeft] = useState([]);
  const [right, setRight] = useState([]);
  const [selected, setSelected] = useState(null);
  // Move selected items from left to right
  // Handles selection (only one item can be selected at a time)
  const handleSelect = (item) => {
    setSelected(selected?.id === item.id ? null : item); // Toggle selection (single select only)
  };

  const moveRight = () => {
    if (selected && left.includes(selected)) {
      // If an item already exists in the right list, move it back to the left
      if (right.length > 0) {
        setLeft((prevLeft) => [...prevLeft, right[0]]); // Move the existing right item back to left
      }

      // Move the selected item to the right list (only one item allowed)
      setRight([selected]);
      setLeft((prevLeft) => prevLeft.filter((item) => item.id !== selected.id)); // Remove from left
      setSelected(null); // Reset selection
    }
  };

  const moveLeft = () => {
    if (selected && right.includes(selected)) {
      // Move the selected item back to the left list
      setLeft((prevLeft) => [...prevLeft, selected]);
      setRight([]); // Empty the right list
      setSelected(null); // Reset selection
    }
  };

  const handleClickOpen = () => {
    //if parent ticket found not required to open dual list item
    var parentId = "";
    var foundMultipleParents = false;
    mergeTicketsData.map((item) => {

      if (item.status === STATUS_CLOSED) {
        showAlert("Found Closed tickets for merge!", "error");
        setDisableDialog(true);
        return true;
      }
      else if (item.mergeTicket === MERGED && parentId === "") {
        parentId = item.id;
      }
      else if (item.mergeTicket === MERGED && parentId !== "") {
        foundMultipleParents = true;
        showAlert("Found multiple parent tickets!", "error");
        return true;
      } else if (item.mergeTicket !== MERGED) {
        childItems.current.push(item);
      }
    });

    if (parentId !== "" && foundMultipleParents === true) {
      return true;
    }
    if (parentId !== "" && foundMultipleParents === false) {
      var tkt = {};
      tkt.mergeTicket = parentId;
      tkt.tickets = childItems.current.map((item) => item.id);
      saveMergeTickets(tkt);
      return true;
    }

    //if parent ticket not found

    setOpen(true);
    setLeft(mergeTicketsData);
    setRight([]);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const mergeTickets = () => {
    var tkt = {};
    tkt.mergeTicket = right[0].id;
    //tkt.tickets = left;
    tkt.tickets = left.map((item) => item.id);
    saveMergeTickets(tkt);
  };

  const saveMergeTickets = (tkt) => {
    AxiosInstance.post(MERGE_TICKETS_URL, tkt)
      .then((response) => {
        if (response.data) {
          showAlert(
            "Tickets merged successfully to " + tkt.mergeTicket + " !",
            "success"
          );
        } else {
          showAlert("Tickets merge failed !", "error");
        }
        callTickets();
      })
      .catch((error) => {
        showAlert("Tickets merge failed !", "error");
        console.error(error);
      });
  };

  // Renders a list of items with single selection
  const renderList = (items, name) => (
    <Paper sx={{ width: 400, height: 350, overflow: "auto", borderRadius: 3 }}>
      <List
        subheader={
          <ListSubheader
            sx={{
              bgcolor: "#1976D2",
              color: "white",
              fontSize: "1.1rem",
              fontWeight: "bold",
            }}
          >
            {name}
          </ListSubheader>
        }
      >
        {items.map((item) => (
          <ListItem
            key={item.id}
            button
            onClick={() => handleSelect(item)}
            sx={{
              fontSize: "10",
              backgroundColor: selected === item ? "lightblue" : "transparent",
              "&:hover": { backgroundColor: "lightgray" },
            }}
          >
            <ListItemText primary={item.id} />
          </ListItem>
        ))}
      </List>
    </Paper>
  );

  return (
    <ThemeProvider theme={theme}>
      <React.Fragment>
        {/* <Button variant="outlined" onClick={handleClickOpen} style={{float:"right", margin:"2px"}}>
              Merge Tickets
            </Button> */}
        {userRole === ROLE_USER ? null : (
          <Tooltip
            title="Merge Tickets"
            onClick={handleClickOpen}
            style={{ cursor: "pointer", margin: "2px" }}
          >
            <IconButton>
              <MergeIcon color="primary" />
            </IconButton>
          </Tooltip>
        )}

        <Dialog
          open={open}
          onClose={handleClose}
          PaperProps={{
            component: "form",
            onSubmit: (event) => {
              event.preventDefault();
              mergeTickets();
              handleClose();
            },
          }}
        >
          <DialogTitle>Merge Tickets</DialogTitle>
          <DialogContent>
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              gap={2}
              p={4}
            >
              {/* Left List - Available Items */}

              {renderList(left, "Merge Tickets")}

              {/* Buttons for moving items */}
              <Box display="flex" flexDirection="column" gap={2}>
                <Button
                  variant="contained"
                  onClick={moveRight}
                  disabled={!selected || !left.includes(selected) || disableDialog}
                >
                  &gt;
                </Button>
                <Button
                  variant="contained"
                  onClick={moveLeft}
                  disabled={!selected || !right.includes(selected) || disableDialog}
                >
                  &lt;
                </Button>
              </Box>

              {/* Right List - Selected Items */}

              {renderList(right, "Parent Ticket")}
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleClose}>Cancel</Button>
            <Button type="submit" disabled={disableDialog}>Merge</Button>
          </DialogActions>
        </Dialog>
      </React.Fragment>
    </ThemeProvider>
  );
};

export default MergeTicketDialog;
